// Sprint 20.3 PR-8 (§H.1) — admin runners panel.
//
// Renders the operator-facing surface of the multi-runner topology:
// every registered runner, its current capacity / status, and the
// drain / re-enable affordances that UC-25 + EC-25/EC-26 require.
//
// Wired by `SettingsPage.tsx` as a `<RunnersSection>` (the page uses
// sections, not tabs — the spec says "tab" but the existing surface is
// a sectioned scroll-page, so we follow the established pattern).
// Auth-gated by `requireAuth` server-side; per-user admin ACL lands in
// S20.4 multi-user work (single-user dashboard today, so render is
// unconditional client-side).
//
// Founder-mode constraints (per `founder-first-vision.md` §5):
//   - Status badges in plain language ("Online", "Offline", "Draining").
//   - Drain confirm modal explains the action: "Drain stops new
//     agents from being scheduled to {hostname} and waits for
//     in-flight agents to finish before the runner exits cleanly."
//     No code, no jargon ("SIGTERM", "WSS dispatch") visible.
//   - `unavailable_reason` rendered as a per-row banner with a
//     prescriptive next-step + "Re-enable" button.
//
// Cursor-ux-mirror constraint (per `cursor-ux-mirror.md` §2):
//   - Tabular data with one runner per row. Cursor's Settings →
//     "Models" panel uses the same row+inline-action pattern.
//   - Loading spinner inline with the action button rather than a
//     full-table overlay (Cursor never blocks the entire table).
//
// Design discipline (per `design-discipline.md`):
//   - Every colour, spacing and radius reaches for an existing role; raw
//     pixel and raw hex classes are a bug, and Gate AA is the ratchet.
//     (DS.7: this line used to spell two of those forbidden classes out as
//     PROSE examples, which the gate counts as occurrences — it cannot tell a
//     class from a sentence about one, and the file carried an arb-px
//     baseline row for a comment. The examples are gone; the rule is not.)
//   - The drain-confirm dialog reuses `<ConfirmDialog>`, which since DS.7
//     renders on `@ds/overlays/Modal` with `role="alertdialog"`.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from '../../i18n';
import { Badge } from '../atoms/Badge';
// DS.7 D17 — the row actions move to the DS piece; the rows themselves stay
// a product block (drain / undrain / re-enable + the per-row unavailable
// banner have no DS equivalent, and `@ds/data-display/Table`'s sortable
// density model cannot carry the banner).
import Button from '@ds/actions/Button';
import { Icon } from '../atoms/Icon';
import { ConfirmDialog } from '../ConfirmDialog';
import { useToastStore } from '../../stores/toast.store';
import {
  fetchRunners,
  drainRunner,
  undrainRunner,
  clearRunnerUnavailable,
  fetchRunnerMetrics,
  type RunnerSnapshot,
  type RunnerStatus,
  type RunnerUnavailableReason,
  type RunnerMetricsSample,
} from '../../lib/api';
import { formatRelativeTime, isStale } from '../../lib/time';
import { RunnerMetricsSparkline } from './RunnerMetricsSparkline';

const POLL_INTERVAL_MS = 10_000;
// Sprint fleet-resource-metrics (spec §2 D9) — one heartbeat cycle. The
// sparkline data changes far less urgently than status/drain state, so
// it polls slower than the table's own 10s cadence rather than adding
// to the same emitter (SSoT audit §6 — a distinct, slower cadence for a
// distinct, slower-changing signal).
const METRICS_POLL_INTERVAL_MS = 30_000;
const SONNET_WEEKLY_LIMIT_HOURS = 240;
const OPUS_WEEKLY_LIMIT_HOURS = 24;

type ToastTKey = Parameters<ReturnType<typeof useTranslation>['t']>[0];

const STATUS_BADGE_COLOR: Record<RunnerStatus, 'success' | 'error' | 'warning' | 'muted'> = {
  online: 'success',
  offline: 'muted',
  draining: 'warning',
  incompatible_version: 'error',
};

const STATUS_LABEL_KEY: Record<RunnerStatus, ToastTKey> = {
  online: 'settings.runners.statusOnline',
  offline: 'settings.runners.statusOffline',
  draining: 'settings.runners.statusDraining',
  incompatible_version: 'settings.runners.statusIncompatible',
};

const UNAVAILABLE_LABEL_KEY: Record<RunnerUnavailableReason, ToastTKey> = {
  claude_account_blocked: 'settings.runners.unavailableReasonBlocked',
  disk_full: 'settings.runners.unavailableReasonDiskFull',
  claude_not_authenticated: 'settings.runners.unavailableReasonNotAuthed',
  incompatible_version: 'settings.runners.unavailableReasonIncompatible',
  // Sprint runner-resilience-after-missing-credential §2.4 — added
  // when closing the backend↔frontend enum drift.
  missing_credential: 'settings.runners.unavailableReasonMissingCredential',
  // Sprint runner-op-token-single-source (defect 3) — workspace drift
  // detected by `op whoami` against the expected santi-team workspace.
  wrong_workspace: 'settings.runners.unavailableReasonWrongWorkspace',
};

export interface RunnersTableProps {
  // Optional injection for stories + tests so the panel renders against
  // a fixed snapshot without exercising the network. Production wires
  // nothing and the component drives `fetchRunners()` itself.
  initialRunners?: RunnerSnapshot[];
  // Optional disable polling — used by stories and unit tests to keep
  // the rendered output deterministic.
  disablePolling?: boolean;
  // Sprint fleet-resource-metrics (spec §2 D10) — optional injection
  // (keyed by runner id) so stories + tests can render the sparkline
  // deterministically without exercising the network. Production wires
  // nothing; each row fetches its own history via `fetchRunnerMetrics`.
  initialMetricsByRunnerId?: Record<string, RunnerMetricsSample[]>;
}

export function RunnersTable({
  initialRunners,
  disablePolling,
  initialMetricsByRunnerId,
}: RunnersTableProps = {}) {
  const { t } = useTranslation();
  const [runners, setRunners] = useState<RunnerSnapshot[] | null>(initialRunners ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-row "I'm doing something" lock so the founder can't double-click
  // drain on the same row but can act on a different row in parallel.
  const [busyRunnerId, setBusyRunnerId] = useState<string | null>(null);
  const [drainConfirm, setDrainConfirm] = useState<RunnerSnapshot | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchRunners();
      setRunners(res.runners);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.runners.loadError'));
      // Don't blank the existing table on a refetch failure — keep
      // showing the last successful snapshot so the founder doesn't
      // lose context.
      if (!runners) setRunners([]);
    } finally {
      setLoading(false);
    }
  }, [t, runners]);

  useEffect(() => {
    if (initialRunners) return; // story/test path — never poll
    void reload();
    if (disablePolling) return;
    const tabVisible = () => document.visibilityState === 'visible';
    const id = window.setInterval(() => {
      if (tabVisible()) void reload();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
    // We deliberately depend ONLY on the immutable `disablePolling`
    // flag and the mount path. `reload` recomputes whenever the runner
    // list changes, but re-binding the interval on every snapshot
    // would defeat the polling cadence. Re-listing is driven by the
    // interval itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disablePolling]);

  const handleDrainClick = (runner: RunnerSnapshot) => {
    setDrainConfirm(runner);
  };

  const handleDrainConfirm = async () => {
    const target = drainConfirm;
    if (!target) return;
    setBusyRunnerId(target.id);
    try {
      const res = await drainRunner(target.id);
      // Optimistic local update so the badge flips immediately even
      // before the next poll lands.
      setRunners((prev) =>
        (prev ?? []).map((r) => (r.id === target.id ? res.runner : r)),
      );
      useToastStore.getState().push({
        message: t('settings.runners.drainSuccess', { hostname: target.hostname }),
      });
    } catch (err) {
      const reason = err instanceof Error ? err.message : t('settings.runners.loadError');
      useToastStore.getState().push({
        message: t('settings.runners.drainError', { hostname: target.hostname, reason }),
        durationMs: 8_000,
      });
    } finally {
      setBusyRunnerId(null);
      setDrainConfirm(null);
    }
  };

  const handleUndrain = async (runner: RunnerSnapshot) => {
    setBusyRunnerId(runner.id);
    try {
      const res = await undrainRunner(runner.id);
      setRunners((prev) => (prev ?? []).map((r) => (r.id === runner.id ? res.runner : r)));
      useToastStore.getState().push({
        message: t('settings.runners.undrainSuccess', { hostname: runner.hostname }),
      });
    } catch (err) {
      const reason = err instanceof Error ? err.message : t('settings.runners.loadError');
      useToastStore.getState().push({
        message: t('settings.runners.undrainError', { hostname: runner.hostname, reason }),
        durationMs: 8_000,
      });
    } finally {
      setBusyRunnerId(null);
    }
  };

  const handleClearUnavailable = async (runner: RunnerSnapshot) => {
    setBusyRunnerId(runner.id);
    try {
      const res = await clearRunnerUnavailable(runner.id);
      setRunners((prev) => (prev ?? []).map((r) => (r.id === runner.id ? res.runner : r)));
      useToastStore.getState().push({
        message: t('settings.runners.clearUnavailableSuccess', { hostname: runner.hostname }),
      });
    } catch (err) {
      const reason = err instanceof Error ? err.message : t('settings.runners.loadError');
      useToastStore.getState().push({
        message: t('settings.runners.clearUnavailableError', {
          hostname: runner.hostname,
          reason,
        }),
        durationMs: 8_000,
      });
    } finally {
      setBusyRunnerId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="runners-panel" aria-busy={loading}>
      {error && (
        <p
          role="alert"
          className="text-caption text-status-error"
          data-testid="runners-error"
        >
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <Button
          variant="text"
          size="sm"
          onClick={() => void reload()}
          disabled={loading}
          className="gap-1.5 text-caption text-muted-foreground hover:text-foreground"
          aria-label={t('settings.runners.reload')}
          data-testid="runners-refresh"
        >
          <Icon name="RefreshCw" size={14} aria-hidden="true" />
          {t('settings.runners.refresh')}
        </Button>
      </div>

      {runners === null ? (
        <p
          className="text-caption text-muted-foreground rounded-md border border-border/50 bg-card p-3"
          data-testid="runners-loading"
        >
          {t('settings.runners.refresh')}
        </p>
      ) : runners.length === 0 ? (
        <p
          className="text-caption text-muted-foreground rounded-md border border-border/50 bg-card p-3"
          data-testid="runners-empty"
        >
          {t('settings.runners.empty')}
        </p>
      ) : (
        <ul
          className="space-y-2"
          data-testid="runners-list"
        >
          {runners.map((runner) => (
            <RunnerRow
              key={runner.id}
              runner={runner}
              busy={busyRunnerId === runner.id}
              onDrain={() => handleDrainClick(runner)}
              onUndrain={() => void handleUndrain(runner)}
              onClearUnavailable={() => void handleClearUnavailable(runner)}
              disablePolling={disablePolling}
              initialMetrics={initialMetricsByRunnerId?.[runner.id]}
            />
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={drainConfirm !== null}
        title={
          drainConfirm
            ? t('settings.runners.confirmDrainTitle', { hostname: drainConfirm.hostname })
            : ''
        }
        body={
          drainConfirm
            ? t('settings.runners.confirmDrainBody', { hostname: drainConfirm.hostname })
            : undefined
        }
        confirmLabel={t('settings.runners.confirmDrain')}
        destructive
        loading={busyRunnerId === drainConfirm?.id}
        onConfirm={() => void handleDrainConfirm()}
        onCancel={() => setDrainConfirm(null)}
      />
    </div>
  );
}

interface RunnerRowProps {
  runner: RunnerSnapshot;
  busy: boolean;
  onDrain: () => void;
  onUndrain: () => void;
  onClearUnavailable: () => void;
  // Sprint fleet-resource-metrics (spec §2 D9/D10).
  disablePolling?: boolean;
  initialMetrics?: RunnerMetricsSample[];
}

function RunnerRow({
  runner,
  busy,
  onDrain,
  onUndrain,
  onClearUnavailable,
  disablePolling,
  initialMetrics,
}: RunnerRowProps) {
  const { t } = useTranslation();
  // Sprint fleet-resource-metrics (spec §2 D9) — per-row sparkline
  // history. `initialMetrics` short-circuits the fetch (stories +
  // tests, same convention as the table-level `initialRunners`); when
  // absent, the row fetches its own 6h window on mount and refreshes on
  // a slower cadence than the table's own status poll (see
  // METRICS_POLL_INTERVAL_MS above).
  const [metrics, setMetrics] = useState<RunnerMetricsSample[] | null>(initialMetrics ?? null);
  useEffect(() => {
    if (initialMetrics) return; // story/test path — never fetch
    if (disablePolling) return; // unit-test default — keep output deterministic
    let cancelled = false;
    const load = () => {
      fetchRunnerMetrics(runner.id)
        .then((res) => {
          if (!cancelled) setMetrics(res.samples);
        })
        .catch(() => {
          // Best-effort — a failed metrics fetch never blocks the rest
          // of the row; the sparkline degrades to its empty state.
        });
    };
    load();
    const id = window.setInterval(load, METRICS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [runner.id, initialMetrics, disablePolling]);
  // Backend stores drain as an orthogonal flag from status (per
  // `runners.repo.ts:227` "We do not flip status; drain is an
  // orthogonal axis"). The founder, however, needs ONE word for the
  // badge, so we project the two flags into one effective state here:
  // an online runner with drain_mode=1 surfaces as 'draining' visually,
  // not 'online'. Without this projection, a drained runner would show
  // the green "Online" dot and the founder couldn't tell which runner
  // is actually draining at a glance.
  const effectiveStatus: RunnerStatus =
    runner.status === 'online' && runner.drainMode ? 'draining' : runner.status;
  const statusColor = STATUS_BADGE_COLOR[effectiveStatus];
  const statusLabel = t(STATUS_LABEL_KEY[effectiveStatus]);
  const canDrain = runner.status === 'online' && !runner.drainMode;
  const canUndrain = runner.drainMode;

  // Sprint runner-checkout-freshness (ADR D-1073, VISIBILITY half) —
  // D-1035: NULL (never reported) is a distinct state from "stale
  // reported value" — only the latter gets the muted "(stale)" marker.
  // A dead daemon's last-known checkout value renders CONTEXTUALIZED as
  // stale, never as if it were current.
  const hasCheckoutData = runner.checkoutBlockerReason !== null;
  const checkoutStale =
    hasCheckoutData && (effectiveStatus !== 'online' || isStale(runner.lastSeen));
  const checkoutSummary = t('settings.runners.checkoutSummary', {
    commits: runner.checkoutStalenessCommits ?? 0,
    repo: runner.checkoutBlockerRepo ?? '',
  });
  const checkoutValue = !hasCheckoutData
    ? t('settings.runners.capUnknown')
    : checkoutStale
      ? `${checkoutSummary} ${t('settings.runners.checkoutStale')}`
      : checkoutSummary;

  return (
    <li
      className="rounded-md border border-border/50 bg-card p-3"
      data-testid="runner-row"
      data-runner-id={runner.id}
      data-status={runner.status}
      data-drain-mode={runner.drainMode ? 'on' : 'off'}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Badge
            variant="dot"
            color={statusColor}
            className="w-2.5 h-2.5 shrink-0"
            aria-label={statusLabel}
          />
          <div className="flex-1 min-w-0">
            <div className="text-body text-foreground font-550 truncate">
              {runner.hostname}
            </div>
            <div className="text-caption text-muted-foreground truncate">
              {runner.id}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canDrain && (
            <Button
              variant="outlined"
              size="sm"
              onClick={onDrain}
              disabled={busy}
              data-testid="runner-row-drain"
            >
              {t('settings.runners.drain')}
            </Button>
          )}
          {canUndrain && (
            <Button
              variant="outlined"
              size="sm"
              onClick={onUndrain}
              disabled={busy}
              data-testid="runner-row-undrain"
            >
              {t('settings.runners.undrain')}
            </Button>
          )}
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-caption">
        <Stat
          label={t('settings.runners.colStatus')}
          value={statusLabel}
          testId="runner-row-status"
        />
        <Stat
          label={t('settings.runners.colAccount')}
          value={runner.claudeAccountId ?? t('settings.runners.capUnknown')}
          testId="runner-row-account"
        />
        <Stat
          label={t('settings.runners.colCap')}
          value={
            runner.cap5hResidualPct === null
              ? t('settings.runners.capUnknown')
              : `${Math.round(100 - runner.cap5hResidualPct)}%`
          }
          testId="runner-row-cap"
        />
        <Stat
          label={t('settings.runners.colWeeklySonnet')}
          value={
            runner.weeklyHoursSonnet === null
              ? t('settings.runners.capUnknown')
              : t('settings.runners.hoursOfLimit', {
                  value: runner.weeklyHoursSonnet,
                  limit: SONNET_WEEKLY_LIMIT_HOURS,
                })
          }
          testId="runner-row-weekly-sonnet"
        />
        <Stat
          label={t('settings.runners.colWeeklyOpus')}
          value={
            runner.weeklyHoursOpus === null
              ? t('settings.runners.capUnknown')
              : t('settings.runners.hoursOfLimit', {
                  value: runner.weeklyHoursOpus,
                  limit: OPUS_WEEKLY_LIMIT_HOURS,
                })
          }
          testId="runner-row-weekly-opus"
        />
        <Stat
          label={t('settings.runners.colActive')}
          value={t('settings.runners.activeAgents', { count: runner.activeAgents })}
          testId="runner-row-active"
        />
        <Stat
          label={t('settings.runners.colCpu')}
          value={
            runner.cpuPct === null
              ? t('settings.runners.capUnknown')
              // D1 — cpuPct has no upper bound on the wire (a loaded box
              // can read >100%); the UI clamps at render.
              : `${Math.round(Math.min(100, runner.cpuPct))}%`
          }
          testId="runner-row-cpu"
        />
        <Stat
          label={t('settings.runners.colRam')}
          value={
            runner.ramPctUsed === null
              ? t('settings.runners.capUnknown')
              : `${Math.round(runner.ramPctUsed)}%`
          }
          testId="runner-row-ram"
        />
        <Stat
          label={t('settings.runners.colDisk')}
          value={
            runner.diskPctUsed === null
              ? t('settings.runners.capUnknown')
              : `${Math.round(runner.diskPctUsed)}%`
          }
          testId="runner-row-disk"
        />
        <Stat label={t('settings.runners.colTrend')} testId="runner-row-trend">
          <RunnerMetricsSparkline samples={metrics ?? []} />
        </Stat>
        <Stat
          label={t('settings.runners.colVersion')}
          value={`v${runner.protocolVersion}`}
          testId="runner-row-version"
        />
        <Stat
          label={t('settings.runners.colLastSeen')}
          value={
            runner.lastSeen
              ? formatRelativeTime(runner.lastSeen)
              : t('settings.runners.lastSeenNever')
          }
          testId="runner-row-last-seen"
        />
        <Stat
          label={t('settings.runners.colCheckout')}
          value={checkoutValue}
          testId="runner-row-checkout"
          muted={checkoutStale}
        />
      </dl>

      {runner.unavailableReason !== null && (
        <div
          role="alert"
          className="mt-3 flex items-start gap-3 rounded-md border border-status-error/30 bg-status-error/10 px-3 py-2"
          data-testid="runner-row-unavailable-banner"
          data-reason={runner.unavailableReason}
        >
          <span className="flex-1 text-caption text-foreground">
            {t(UNAVAILABLE_LABEL_KEY[runner.unavailableReason])}
          </span>
          <Button
            variant="outlined"
            size="sm"
            onClick={onClearUnavailable}
            disabled={busy}
            className="shrink-0 text-status-error border-status-error/40 hover:bg-status-error/10"
            data-testid="runner-row-reenable"
          >
            {t('settings.runners.reEnable')}
          </Button>
        </div>
      )}
    </li>
  );
}

function Stat({
  label,
  value,
  testId,
  muted,
  children,
}: {
  label: string;
  // Optional when `children` supplies a non-string value (Sprint
  // fleet-resource-metrics — the sparkline `<Stat>` cell renders an
  // SVG, not text).
  value?: string;
  testId?: string;
  // Sprint runner-checkout-freshness (ADR D-1073) — renders the value
  // in the same muted token the loading/empty states already use, so a
  // stale reading never reads as visually equivalent to a fresh one.
  muted?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col min-w-0" data-testid={testId}>
      <dt className="text-label uppercase text-muted-foreground">{label}</dt>
      <dd className={`text-caption truncate ${muted ? 'text-muted-foreground' : 'text-foreground'}`}>
        {children ?? value}
      </dd>
    </div>
  );
}
